'use client';

import React, { useState, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';

// Recharts isolated in client-only dynamic import to avoid SSR issues
const AssessCharts = dynamic(() => import('./AssessCharts'), { ssr: false });

// ─── Types ────────────────────────────────────────────────────────────────────

interface AssessInputs {
  appsMaintained: string;
  appsRetirement: string;
  avgAnnualCost: string;
  remainingMonths: string;
  programMonths: string;
}

interface AssessResults {
  currentAnnualCost: number;
  currentMonthlyCost: number;
  inScopeAnnualCost: number;
  inScopeMonthlyCost: number;
  remainingCommitment: number;
  spendUntilRetirement: number;
  futureMaintAvoided: number;
  retirementScopePct: number;
  costReductionPct: number;
}

interface ValidationState {
  errors: Record<string, string>;
  warnings: Record<string, string>;
  isValid: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(val: number): string {
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(2)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(1)}K`;
  return `$${val.toLocaleString()}`;
}

function fmtFull(val: number): string {
  return `$${Math.round(val).toLocaleString()}`;
}

// ─── Metric Tile ─────────────────────────────────────────────────────────────

function MetricTile({
  label,
  value,
  sub,
  accent,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className="rounded-xl p-4 flex flex-col gap-1 transition-all duration-200"
      style={{
        background: highlight ? 'rgba(255,107,107,0.08)' : 'rgba(255,255,255,0.04)',
        border: highlight ? '1px solid rgba(255,107,107,0.25)' : '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <p className="text-xs font-semibold tracking-wide uppercase" style={{ color: '#A8B2C1', letterSpacing: '0.07em', fontSize: '10px' }}>
        {label}
      </p>
      <p className="font-mono-data font-bold text-2xl leading-tight" style={{ color: accent || '#FFFFFF' }}>
        {value}
      </p>
      {sub && <p className="text-xs" style={{ color: '#A8B2C1' }}>{sub}</p>}
    </div>
  );
}

// ─── Input Field ─────────────────────────────────────────────────────────────

function InputField({
  label,
  helpText,
  value,
  onChange,
  placeholder,
  prefix,
  suffix,
  error,
  warning,
}: {
  label: string;
  helpText?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  prefix?: string;
  suffix?: string;
  error?: string;
  warning?: string;
}) {
  const hasError = !!error;
  const hasWarning = !hasError && !!warning;

  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5 tracking-wide uppercase" style={{ color: '#A8B2C1', letterSpacing: '0.07em' }}>
        {label}
      </label>
      {helpText && <p className="text-xs mb-1.5" style={{ color: '#A8B2C1' }}>{helpText}</p>}
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-mono-data" style={{ color: '#A8B2C1' }}>
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`input-base ${hasError ? 'input-error' : ''} ${hasWarning ? 'input-warning' : ''}`}
          style={{ paddingLeft: prefix ? '1.75rem' : undefined, paddingRight: suffix ? '3rem' : undefined }}
          min="0"
          suppressHydrationWarning
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: '#A8B2C1' }}>
            {suffix}
          </span>
        )}
      </div>
      {hasError && <p className="text-xs mt-1.5" style={{ color: '#EF4444' }}>{error}</p>}
      {hasWarning && <p className="text-xs mt-1.5" style={{ color: '#F59E0B' }}>{warning}</p>}
    </div>
  );
}

// ─── Snackbar ─────────────────────────────────────────────────────────────────

function Snackbar({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-lg shadow-xl" style={{ background: '#1A2E42', border: '1px solid rgba(78,205,196,0.3)', minWidth: '280px' }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4ECDC4" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
      <span className="text-sm font-medium text-white">{message}</span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AssessCalculator() {
  const [inputs, setInputs] = useState<AssessInputs>({
    appsMaintained: '',
    appsRetirement: '',
    avgAnnualCost: '',
    remainingMonths: '',
    programMonths: '',
  });

  const [results, setResults] = useState<AssessResults | null>(null);
  const [validation, setValidation] = useState<ValidationState>({ errors: {}, warnings: {}, isValid: false });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [currentDate, setCurrentDate] = useState('');

  useEffect(() => {
    const raw = localStorage.getItem('smarck_business_profile');
    if (raw) {
      try {
        const profile = JSON.parse(raw);
        if (profile.appPortfolioSize > 0) {
          setInputs((p) => ({ ...p, appsMaintained: String(profile.appPortfolioSize) }));
        }
      } catch {}
    }
    const d = new Date();
    setCurrentDate(`${d.toLocaleString('default', { month: 'long' })} ${d.getDate()}, ${d.getFullYear()}`);
  }, []);

  const validate = useCallback((inp: AssessInputs): ValidationState => {
    const errors: Record<string, string> = {};
    const warnings: Record<string, string> = {};

    const maintained = parseFloat(inp.appsMaintained);
    const retirement = parseFloat(inp.appsRetirement);
    const cost = parseFloat(inp.avgAnnualCost);
    const remaining = parseFloat(inp.remainingMonths);
    const program = parseFloat(inp.programMonths);

    if (!inp.appsMaintained || maintained <= 0) errors.appsMaintained = 'Must be greater than 0.';
    if (!inp.appsRetirement || retirement <= 0) errors.appsRetirement = 'Must be greater than 0.';
    if (!inp.avgAnnualCost || cost <= 0) errors.avgAnnualCost = 'Must be greater than 0.';
    if (!inp.remainingMonths || remaining <= 0) errors.remainingMonths = 'Must be greater than 0.';
    if (!inp.programMonths || program <= 0) errors.programMonths = 'Must be greater than 0.';

    if (!errors.appsMaintained && !errors.appsRetirement && retirement > maintained) {
      errors.appsRetirement = 'Cannot exceed total apps maintained.';
    }
    if (!errors.remainingMonths && !errors.programMonths && program > remaining) {
      errors.programMonths = 'Program duration cannot exceed remaining maintenance period.';
    }

    if (!errors.avgAnnualCost && cost < 5_000) warnings.avgAnnualCost = 'Cost below $5,000 is unusually low for enterprise applications.';
    if (!errors.avgAnnualCost && cost > 500_000) warnings.avgAnnualCost = 'Cost above $500,000 per app is unusually high — verify this figure.';
    if (!errors.appsMaintained && maintained > 10_000) warnings.appsMaintained = 'Portfolio of 10,000+ apps is unusually large — confirm this figure.';
    if (!errors.remainingMonths && remaining > 240) warnings.remainingMonths = 'Maintenance period over 20 years — verify this figure.';
    if (!errors.programMonths && program > 120) warnings.programMonths = 'Program duration over 10 years — verify this figure.';

    return {
      errors,
      warnings,
      isValid: Object.keys(errors).length === 0,
    };
  }, []);

  const calculate = useCallback((inp: AssessInputs): AssessResults | null => {
    const maintained = parseFloat(inp.appsMaintained);
    const retirement = parseFloat(inp.appsRetirement);
    const cost = parseFloat(inp.avgAnnualCost);
    const remaining = parseFloat(inp.remainingMonths);
    const program = parseFloat(inp.programMonths);

    if ([maintained, retirement, cost, remaining, program].some((v) => isNaN(v) || v <= 0)) return null;
    if (retirement > maintained || program > remaining) return null;

    const currentAnnualCost = maintained * cost;
    const currentMonthlyCost = currentAnnualCost / 12;
    const inScopeAnnualCost = retirement * cost;
    const inScopeMonthlyCost = inScopeAnnualCost / 12;
    const remainingCommitment = inScopeAnnualCost * (remaining / 12);
    const spendUntilRetirement = inScopeMonthlyCost * program;
    const savingsPeriod = (remaining - program) / 12;
    const futureMaintAvoided = savingsPeriod > 0 ? inScopeAnnualCost * savingsPeriod : 0;
    const retirementScopePct = (retirement / maintained) * 100;
    const costReductionPct = remainingCommitment > 0 ? (futureMaintAvoided / remainingCommitment) * 100 : 0;

    return {
      currentAnnualCost,
      currentMonthlyCost,
      inScopeAnnualCost,
      inScopeMonthlyCost,
      remainingCommitment,
      spendUntilRetirement,
      futureMaintAvoided,
      retirementScopePct,
      costReductionPct,
    };
  }, []);

  useEffect(() => {
    const v = validate(inputs);
    setValidation(v);
    if (v.isValid) {
      setResults(calculate(inputs));
    } else {
      setResults(null);
    }
  }, [inputs, validate, calculate]);

  const setInput = (key: keyof AssessInputs) => (val: string) => {
    setInputs((p) => ({ ...p, [key]: val }));
  };

  const handleExportPDF = () => {
    if (!validation.isValid) return;
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleClearSession = () => {
    setInputs({ appsMaintained: '', appsRetirement: '', avgAnnualCost: '', remainingMonths: '', programMonths: '' });
    setResults(null);
    setShowSnackbar(true);
  };

  const maintained = parseFloat(inputs.appsMaintained) || 0;
  const retirement = parseFloat(inputs.appsRetirement) || 0;
  const program = parseFloat(inputs.programMonths) || 0;

  return (
    <>
      <div className="no-print">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#4ECDC4', letterSpacing: '0.1em' }}>
                ROI CALCULATOR
              </span>
            </div>
            <h1 className="text-2xl font-bold text-white">Retirement Program Assessment</h1>
            <p className="text-sm mt-1" style={{ color: '#A8B2C1' }}>
              Real-time financial modelling for application retirement programs.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleClearSession} className="btn-ghost text-xs">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.1"/>
              </svg>
              Clear
            </button>
            <button
              onClick={handleExportPDF}
              disabled={!validation.isValid}
              className="btn-primary text-sm"
              title={!validation.isValid ? 'Resolve all validation errors before exporting' : 'Export Assess Report as PDF'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export Assess Report
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          {/* ── Input Panel ───────────────────────────────────────────────── */}
          <div className="xl:col-span-2">
            <div className="rounded-xl p-6 sticky top-24" style={{ background: '#111F30', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h2 className="text-xs font-bold tracking-widest uppercase mb-5" style={{ color: '#4ECDC4', letterSpacing: '0.1em' }}>
                PROGRAM INPUTS
              </h2>
              <div className="flex flex-col gap-5">
                <InputField
                  label="Apps Maintained"
                  helpText="Total applications currently in the IT portfolio."
                  value={inputs.appsMaintained}
                  onChange={setInput('appsMaintained')}
                  placeholder="e.g. 240"
                  error={validation.errors.appsMaintained}
                  warning={validation.warnings.appsMaintained}
                />
                <InputField
                  label="Apps Selected for Retirement"
                  helpText="Applications in scope for the retirement program."
                  value={inputs.appsRetirement}
                  onChange={setInput('appsRetirement')}
                  placeholder="e.g. 60"
                  error={validation.errors.appsRetirement}
                  warning={validation.warnings.appsRetirement}
                />
                <InputField
                  label="Avg Annual Cost per App (USD)"
                  helpText="Include licensing, infrastructure, and support FTE allocation."
                  value={inputs.avgAnnualCost}
                  onChange={setInput('avgAnnualCost')}
                  placeholder="e.g. 45000"
                  prefix="$"
                  error={validation.errors.avgAnnualCost}
                  warning={validation.warnings.avgAnnualCost}
                />
                <InputField
                  label="Remaining Maintenance Period"
                  helpText="How many more months current contracts/obligations run."
                  value={inputs.remainingMonths}
                  onChange={setInput('remainingMonths')}
                  placeholder="e.g. 36"
                  suffix="mo"
                  error={validation.errors.remainingMonths}
                  warning={validation.warnings.remainingMonths}
                />
                <InputField
                  label="Retirement Program Duration"
                  helpText="How many months to complete the retirement program."
                  value={inputs.programMonths}
                  onChange={setInput('programMonths')}
                  placeholder="e.g. 12"
                  suffix="mo"
                  error={validation.errors.programMonths}
                  warning={validation.warnings.programMonths}
                />
              </div>

              {/* Validation summary */}
              {!validation.isValid && Object.keys(validation.errors).length > 0 && (
                <div className="mt-5 px-3 py-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#EF4444' }}>
                    {Object.keys(validation.errors).length} validation {Object.keys(validation.errors).length === 1 ? 'error' : 'errors'} — resolve to enable export
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ── Results Panel ─────────────────────────────────────────────── */}
          <div className="xl:col-span-3 flex flex-col gap-6">
            {/* Metric tiles */}
            <div>
              <h2 className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#4ECDC4', letterSpacing: '0.1em' }}>
                CALCULATED OUTPUTS
              </h2>
              {results ? (
                <div className="grid grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-4 gap-3">
                  <MetricTile label="Current Annual Cost" value={fmt(results.currentAnnualCost)} sub="Full portfolio" />
                  <MetricTile label="Current Monthly Cost" value={fmt(results.currentMonthlyCost)} sub="Full portfolio" />
                  <MetricTile label="In-Scope Annual Cost" value={fmt(results.inScopeAnnualCost)} sub={`${retirement} retirement apps`} accent="#F59E0B" />
                  <MetricTile label="In-Scope Monthly Cost" value={fmt(results.inScopeMonthlyCost)} sub="Retirement apps" accent="#F59E0B" />
                  <MetricTile label="Remaining Commitment" value={fmt(results.remainingCommitment)} sub="Total in-scope obligation" accent="#F97316" />
                  <MetricTile label="Spend Until Retirement" value={fmt(results.spendUntilRetirement)} sub={`Over ${program} month program`} accent="#FF6B6B" />
                  <MetricTile
                    label="Future Maintenance Avoided"
                    value={results.futureMaintAvoided > 0 ? fmt(results.futureMaintAvoided) : '$0'}
                    sub={results.futureMaintAvoided > 0 ? 'Post-program savings' : 'Program exceeds remaining period'}
                    accent={results.futureMaintAvoided > 0 ? '#4ECDC4' : '#A8B2C1'}
                    highlight={results.futureMaintAvoided > 0}
                  />
                  <div className="rounded-xl p-4 flex flex-col gap-1" style={{ background: 'rgba(78,205,196,0.06)', border: '1px solid rgba(78,205,196,0.2)' }}>
                    <p className="text-xs font-semibold tracking-wide uppercase" style={{ color: '#A8B2C1', letterSpacing: '0.07em', fontSize: '10px' }}>
                      NET ROI SIGNAL
                    </p>
                    <p className="font-mono-data font-bold text-2xl leading-tight" style={{ color: results.futureMaintAvoided > results.spendUntilRetirement ? '#4ECDC4' : '#F59E0B' }}>
                      {results.futureMaintAvoided > results.spendUntilRetirement ? 'POSITIVE' : 'BREAK-EVEN'}
                    </p>
                    <p className="text-xs" style={{ color: '#A8B2C1' }}>
                      {results.futureMaintAvoided > results.spendUntilRetirement
                        ? `${fmt(results.futureMaintAvoided - results.spendUntilRetirement)} net benefit`
                        : 'Extend timeline or reduce scope'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="text-sm" style={{ color: '#A8B2C1' }}>
                    Enter valid inputs to see calculated outputs.
                  </p>
                </div>
              )}
            </div>

            {/* Narrative summary */}
            {results && (
              <div className="rounded-xl p-5" style={{ background: '#111F30', border: '1px solid rgba(255,255,255,0.08)' }}>
                <h3 className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#4ECDC4', letterSpacing: '0.1em' }}>
                  EXECUTIVE SUMMARY
                </h3>

                {/* Scenario 1: Within the retirement program duration */}
                <div className="mb-4 pl-3" style={{ borderLeft: '2px solid rgba(255,107,107,0.5)' }}>
                  <p className="text-xs font-semibold uppercase mb-1.5 tracking-wide" style={{ color: '#FF6B6B', letterSpacing: '0.07em' }}>
                    DURING THE RETIREMENT PROGRAM ({program} months)
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#D1D9E3' }}>
                    While the retirement program is active, your organization will spend{' '}
                    <strong style={{ color: '#FF6B6B' }}>{fmtFull(results.spendUntilRetirement)}</strong> in transitional maintenance costs
                    across the <strong style={{ color: '#FFFFFF' }}>{retirement.toLocaleString()}</strong> in-scope applications.
                    This represents the unavoidable cost commitment before decommissioning is complete —
                    at <strong style={{ color: '#FFFFFF' }}>{fmtFull(results.inScopeMonthlyCost)}</strong> per month for the duration of the program.
                  </p>
                </div>

                {/* Scenario 2: Before the maintenance period ends (retirement completes within contract) */}
                <div className="mb-4 pl-3" style={{ borderLeft: '2px solid rgba(245,158,11,0.5)' }}>
                  <p className="text-xs font-semibold uppercase mb-1.5 tracking-wide" style={{ color: '#F59E0B', letterSpacing: '0.07em' }}>
                    IF RETIRED BEFORE THE MAINTENANCE PERIOD ENDS
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#D1D9E3' }}>
                    Completing retirement within the remaining{' '}
                    <strong style={{ color: '#FFFFFF' }}>{parseFloat(inputs.remainingMonths)} months</strong> of the maintenance window means
                    the remaining <strong style={{ color: '#F59E0B' }}>{(parseFloat(inputs.remainingMonths) - program).toFixed(0)} months</strong> of
                    post-program maintenance costs are avoided entirely.
                    {results.futureMaintAvoided > 0 ? (
                      <>
                        {' '}This unlocks <strong style={{ color: '#4ECDC4' }}>{fmtFull(results.futureMaintAvoided)}</strong> in avoided
                        future maintenance spend — a <strong style={{ color: '#4ECDC4' }}>{results.costReductionPct.toFixed(1)}%</strong> reduction
                        against the total remaining commitment of <strong style={{ color: '#FFFFFF' }}>{fmtFull(results.remainingCommitment)}</strong>.
                      </>
                    ) : (
                      ' However, the program duration matches or exceeds the remaining maintenance period, leaving no post-program savings window.'
                    )}
                  </p>
                </div>

                {/* Scenario 3: After the maintenance period */}
                <div className="pl-3" style={{ borderLeft: '2px solid rgba(78,205,196,0.5)' }}>
                  <p className="text-xs font-semibold uppercase mb-1.5 tracking-wide" style={{ color: '#4ECDC4', letterSpacing: '0.07em' }}>
                    IF RETIRED AFTER THE MAINTENANCE PERIOD ENDS
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#D1D9E3' }}>
                    If retirement is deferred beyond the current maintenance period, the organization will have absorbed the full
                    remaining commitment of <strong style={{ color: '#FF6B6B' }}>{fmtFull(results.remainingCommitment)}</strong> with no cost
                    avoidance benefit. Post-maintenance, the{' '}
                    <strong style={{ color: '#FFFFFF' }}>{retirement.toLocaleString()}</strong> in-scope applications would likely
                    transition to renewed contracts or ad-hoc support arrangements — typically at higher per-app rates —
                    making the annualized cost of <strong style={{ color: '#F59E0B' }}>{fmtFull(results.inScopeAnnualCost)}</strong> a
                    recurring liability rather than an avoidable one. Early retirement within the program window is the
                    financially optimal path.
                  </p>
                </div>
              </div>
            )}

            {/* Charts */}
            {results && (
              <AssessCharts
                retirementScopePct={results.retirementScopePct}
                costReductionPct={results.costReductionPct}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Print-only PDF content ──────────────────────────────────────────── */}
      {results && (
        <div className="print-only">
          {/* Page 1: Cover */}
          <div className="print-page">
            <div className="print-header">
              <div className="print-title">SMARCK-M Retirement Program Assessment</div>
              <div className="print-subtitle">Application Retirement ROI Analysis — {currentDate}</div>
              <div className="print-subtitle" style={{ marginTop: '4px' }}>CLANOMEGA Solutions Private Limited — Session-Only Calculation</div>
            </div>
            <div className="print-section-header">HEADLINE METRICS</div>
            <div className="print-grid-3" style={{ marginBottom: '1rem' }}>
              {[
                { label: 'Current Annual Cost', value: fmtFull(results.currentAnnualCost) },
                { label: 'In-Scope Annual Cost', value: fmtFull(results.inScopeAnnualCost) },
                { label: 'Future Maintenance Avoided', value: fmtFull(results.futureMaintAvoided) },
                { label: 'Spend Until Retirement', value: fmtFull(results.spendUntilRetirement) },
                { label: 'Retirement Scope', value: `${results.retirementScopePct.toFixed(1)}%` },
                { label: 'Cost Reduction %', value: `${results.costReductionPct.toFixed(1)}%` },
              ].map((t) => (
                <div key={`print-assess-${t.label}`} className="print-tile">
                  <div className="print-tile-label">{t.label}</div>
                  <div className="print-tile-value">{t.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Page 2: Dashboard narrative */}
          <div className="print-page">
            <div className="print-header">
              <div className="print-title">SMARCK-M — Assessment Dashboard</div>
              <div className="print-subtitle">{currentDate}</div>
            </div>
            <div className="print-section-header">EXECUTIVE SUMMARY</div>
            <p style={{ fontSize: '10pt', lineHeight: 1.6, color: '#333', marginBottom: '1rem' }}>
              By retiring {retirement.toLocaleString()} of {maintained.toLocaleString()} applications ({results.retirementScopePct.toFixed(1)}% of the portfolio) over {program} months, the organization avoids approximately {fmtFull(results.futureMaintAvoided)} in future maintenance spend. The retirement program investment period of {program} months represents {fmtFull(results.spendUntilRetirement)} in transitional costs before savings begin. The annualized maintenance cost reduction is {fmtFull(results.inScopeAnnualCost)}, representing a {results.costReductionPct.toFixed(1)}% reduction in total portfolio maintenance spend.
            </p>
            <div className="print-section-header">PROGRAM SCOPE INDICATORS</div>
            <div className="print-grid-2">
              <div>
                <div style={{ fontSize: '9pt', color: '#666', marginBottom: '4px' }}>Retirement Scope</div>
                <div style={{ background: '#eee', height: '12px', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={{ background: '#FF6B6B', height: '100%', width: `${Math.min(results.retirementScopePct, 100)}%` }} />
                </div>
                <div style={{ fontSize: '9pt', fontWeight: 700, marginTop: '2px' }}>{results.retirementScopePct.toFixed(1)}%</div>
              </div>
              <div>
                <div style={{ fontSize: '9pt', color: '#666', marginBottom: '4px' }}>Cost Reduction Potential</div>
                <div style={{ background: '#eee', height: '12px', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={{ background: '#4ECDC4', height: '100%', width: `${Math.min(results.costReductionPct, 100)}%` }} />
                </div>
                <div style={{ fontSize: '9pt', fontWeight: 700, marginTop: '2px' }}>{results.costReductionPct.toFixed(1)}%</div>
              </div>
            </div>
          </div>

          {/* Page 3: Inputs & calculation trace */}
          <div className="print-page">
            <div className="print-header">
              <div className="print-title">SMARCK-M — Inputs & Calculation Trace</div>
              <div className="print-subtitle">{currentDate}</div>
            </div>
            <div className="print-section-header">RAW INPUTS</div>
            <table className="print-table" style={{ marginBottom: '1.5rem' }}>
              <thead><tr><th>Input Parameter</th><th>Value</th></tr></thead>
              <tbody>
                {[
                  ['Apps Maintained', inputs.appsMaintained],
                  ['Apps Selected for Retirement', inputs.appsRetirement],
                  ['Avg Annual Cost per App (USD)', `$${parseFloat(inputs.avgAnnualCost).toLocaleString()}`],
                  ['Remaining Maintenance Period', `${inputs.remainingMonths} months`],
                  ['Retirement Program Duration', `${inputs.programMonths} months`],
                ].map(([k, v]) => (
                  <tr key={`print-input-${k}`}><td>{k}</td><td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="print-section-header">CALCULATION TRACE</div>
            <table className="print-table">
              <thead><tr><th>Output Metric</th><th>Formula</th><th>Value</th></tr></thead>
              <tbody>
                {[
                  ['Current Annual Cost', `${inputs.appsMaintained} apps x $${parseFloat(inputs.avgAnnualCost).toLocaleString()}`, fmtFull(results.currentAnnualCost)],
                  ['Current Monthly Cost', 'Annual / 12', fmtFull(results.currentMonthlyCost)],
                  ['In-Scope Annual Cost', `${inputs.appsRetirement} apps x $${parseFloat(inputs.avgAnnualCost).toLocaleString()}`, fmtFull(results.inScopeAnnualCost)],
                  ['In-Scope Monthly Cost', 'In-Scope Annual / 12', fmtFull(results.inScopeMonthlyCost)],
                  ['Remaining Commitment', `In-Scope Annual x (${inputs.remainingMonths} / 12)`, fmtFull(results.remainingCommitment)],
                  ['Spend Until Retirement', `In-Scope Monthly x ${inputs.programMonths} months`, fmtFull(results.spendUntilRetirement)],
                  ['Future Maintenance Avoided', `In-Scope Annual x ((${inputs.remainingMonths} - ${inputs.programMonths}) / 12)`, fmtFull(results.futureMaintAvoided)],
                ].map(([metric, formula, value]) => (
                  <tr key={`print-calc-${metric}`}><td>{metric}</td><td style={{ color: '#666', fontSize: '8pt' }}>{formula}</td><td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{value}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="print-footer">
              Session-only calculation. No data is stored or transmitted. SMARCK-M by CLANOMEGA Solutions Private Limited.
            </div>
          </div>
        </div>
      )}

      {showSnackbar && (
        <Snackbar message="Session cleared. All inputs reset." onClose={() => setShowSnackbar(false)} />
      )}
    </>
  );
}